import styles from './documentPaymentTypeIdsFields.module.css'
import { useEffect, useMemo, useState } from "react"
import { BooleanField, FieldList, FieldType, FieldValue, FormField, O, option, Section, SelectField } from "../../../../features/ui"
import { useCustomerTypeLoaders } from "../../loaders/customerTypeLoaders"
import { DocumentKey, DocumentPaymentTypeIds, DocumentPaymentTypeIdsData, PaymentType, usePaymentTypeLoaders } from '../../../administration'
import { PaymentTypesTable } from '../payment-types-table/paymentTypesTable'

type Props = {
    documentKey?: DocumentKey
    documentPaymentTypeIds?: DocumentPaymentTypeIds
    onSaveData: (documentKey: DocumentKey, data: DocumentPaymentTypeIdsData) => void
}

export const DocumentPaymentTypeIdsFields = (props: Props) => {
    const { documentKey, documentPaymentTypeIds, onSaveData } = props

    const [customerTypeId, setCustomerTypeId] = useState<O<string>>(undefined)
    const [latePaymentOnlyState, setLatePaymentOnlyState] = useState<O<boolean>>(undefined)
    const [paymentTypesState, setPaymentTypesState] = useState<PaymentType[]>([])

    const customerTypeLoaders = useCustomerTypeLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()

    const customerTypeOptions = useMemo(() =>
        customerTypeLoaders().findAllCustomerTypes(true).map(customerType => option(customerType.id, customerType.name))
    , [])

    const renderCustomerTypeInputLabel = (value?: string) => `Lista de Precios: ${customerTypeOptions.find(option => option.value === value)?.label}`

    const documentPaymentTypeIdsData: O<DocumentPaymentTypeIdsData> = useMemo(() =>
        documentPaymentTypeIds && documentKey && documentPaymentTypeIds[documentKey]
    , [documentPaymentTypeIds, documentKey])

    useEffect(() => onChangeCustomerTypeId(undefined), [documentKey])
    
    const onSave = (
        newLatePaymentOnly: boolean,
        newPaymentTypes: PaymentType[]
    ) => {
        const newData: DocumentPaymentTypeIdsData = {
            ...(documentPaymentTypeIdsData || {}),
            [customerTypeId!]: {
                latePaymentOnly: newLatePaymentOnly,
                paymentTypeIds: newPaymentTypes.map(paymentType => paymentType.id)
            }
        }
        onSaveData(documentKey!, newData)
    }

    const onChangeCustomerTypeId = (value?: FieldValue) => {
        const newCustomerTypeId = value as O<string>
        setCustomerTypeId(newCustomerTypeId)
        const documentCustomerTypeData = newCustomerTypeId ? documentPaymentTypeIdsData?.[newCustomerTypeId] : undefined
        setLatePaymentOnlyState(!!documentCustomerTypeData?.latePaymentOnly)
        documentCustomerTypeData?.paymentTypeIds
        const documentPaymentTypes = documentCustomerTypeData?.paymentTypeIds.map(paymentTypeId => paymentTypeLoaders().findPaymentType(paymentTypeId)!) || []
        setPaymentTypesState(documentPaymentTypes)
    }

    const onChangeLatePayment = (value?: FieldValue) => {
        const newLatePaymentOnly = !!value
        setLatePaymentOnlyState(newLatePaymentOnly)
        onSave(newLatePaymentOnly, paymentTypesState)
    }

    const onChangePaymentTypes = (newPaymentTypes: PaymentType[]) => {
        setPaymentTypesState(newPaymentTypes)
        onSave(!!latePaymentOnlyState, newPaymentTypes)
    }

    const fields: FormField[] = [
        {
            name: 'customerTypeId',
            type: FieldType.SELECT,
            render: () => (
                <div className={styles.customerTypeContainer}>
                    <SelectField
                        id="customerTypeId"
                        value={customerTypeId}
                        options={customerTypeOptions}
                        placeholder="Seleccionar Lista de Precios"
                        onChange={onChangeCustomerTypeId}
                        className={styles.customerTypeId}
                        renderInputLabel={renderCustomerTypeInputLabel}
                    />
                    {documentKey === DocumentKey.SALE && customerTypeId && (
                        <BooleanField
                            id="latePaymentOnly"
                            label="Aplicar sólo a Pago contra entrega"
                            value={latePaymentOnlyState}
                            onChange={onChangeLatePayment}
                        />
                    )}
                </div>
            )
        },
        {
            name: 'paymentTypes',
            type: FieldType.TABLE,
            render: () => (
                <PaymentTypesTable
                    rows={paymentTypesState}
                    readOnly={!documentKey || !customerTypeId}
                    onChange={onChangePaymentTypes}
                />
            )
        }
    ]

    return (
        <Section id="documentPaymentTypes" space={12}>
            <FieldList fields={fields} />
        </Section>
    )
}
